import React from "react";
import {
  Calendar,
  CheckCircle,
  User,
  UserCheck,
} from "react-feather";
import { FolderTree } from "lucide-react";
import StatusPill from "../../atoms/StatusPill/StatusPill";
import {
  KG_INSPECTOR_ICON_DEFAULT_SIZE,
  kgInspectorIcons,
} from "../../kg/canvas/inspector/kgInspectorIcons";
import type { KgInspectorMetricVerificationStatus } from "../../kg/canvas/inspector/types";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import { KgInspectorExpandableSection } from "../KgInspectorExpandableSection";
import {
  DescriptionBody,
  EmptyHint,
  IconSlot,
  OverviewRoot,
  SectionBlock,
  SectionHeader,
  SectionTitle,
} from "./KgInspectorMetricOverview.styles";
import type { KgInspectorMetricOverviewProps } from "./KgInspectorMetricOverview.types";

const DescriptionIcon = kgInspectorIcons.description;
const OntologyIcon = kgInspectorIcons.ontologyPanel;
const ontologyIconSize = KG_INSPECTOR_ICON_DEFAULT_SIZE.ontologyPanel;

const VERIFICATION_LABEL: Record<KgInspectorMetricVerificationStatus, string> =
  {
    verified: "Verified",
    unverified: "Unverified",
    pending: "Pending",
  };

const verificationVariant = (
  status: KgInspectorMetricVerificationStatus,
): "success" | "warning" | "neutral" => {
  if (status === "verified") return "success";
  if (status === "pending") return "warning";
  return "neutral";
};

export const KgInspectorMetricOverview: React.FC<
  KgInspectorMetricOverviewProps
> = ({ overview }) => {
  if (!overview) return null;

  const {
    description,
    verificationStatus,
    verifiedBy,
    lastUpdatedBy,
    updatedOn,
    tags,
    ontologies,
  } = overview;

  const verificationLabel = VERIFICATION_LABEL[verificationStatus];

  return (
    <OverviewRoot>
      <SectionBlock>
        <SectionHeader>
          <IconSlot>
            <DescriptionIcon size={16} />
          </IconSlot>
          <SectionTitle>Description</SectionTitle>
        </SectionHeader>
        {description ? (
          <DescriptionBody>{description}</DescriptionBody>
        ) : (
          <EmptyHint>No description available for this metric yet.</EmptyHint>
        )}
      </SectionBlock>

      <KgInspectorDetailRow
        icon={<CheckCircle size={16} />}
        label="Verification status"
        value={
          <StatusPill
            variant={verificationVariant(verificationStatus)}
            showDot={false}
          >
            {verificationLabel}
          </StatusPill>
        }
      />

      {verifiedBy && (
        <KgInspectorDetailRow
          icon={<UserCheck size={16} />}
          label="Verified by"
          value={verifiedBy}
        />
      )}

      {lastUpdatedBy && (
        <KgInspectorDetailRow
          icon={<User size={16} />}
          label="Last updated by"
          value={lastUpdatedBy}
        />
      )}

      {updatedOn && (
        <KgInspectorDetailRow
          icon={<Calendar size={16} />}
          label="Updated on"
          value={updatedOn}
        />
      )}

      {tags.length > 0 && (
        <KgInspectorExpandableSection
          variant="pills"
          icon={<FolderTree size={16} strokeWidth={1.75} />}
          label="Tags"
          items={tags}
          isOpen
        />
      )}

      {ontologies.length > 0 && (
        <KgInspectorExpandableSection
          variant="linkRows"
          icon={<OntologyIcon size={ontologyIconSize} />}
          label="Ontologies"
          items={ontologies}
          isOpen
        />
      )}
    </OverviewRoot>
  );
};

export default KgInspectorMetricOverview;
